import React, { useState, useEffect, useContext } from "react";
import "./LoginCard.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from 'axios';
import { sendLoginOtp, loginWithOtp, loginByGoogle } from '../api/authHandlers';
import { GOOGLE_CLIENT_ID, GOOGLE_LOGIN_ENABLED } from '../config/googleClient';
import { preserveOnboardingDraft, restoreOnboardingDraft, markAccountComplete, hasListingDraftToResume, hasAddPropertyDraftToResume } from '../pages/listing/onboardingDraft';
import { resetHotelListCache, prefetchHotelsAfterAuth } from '../hooks/useHotelList';
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';

const LoginCard = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showForgotPasswordForm, setShowForgotPasswordForm] = useState(false);
  const [showOtpLogin, setShowOtpLogin] = useState(false);
  const [otpLoginEmail, setOtpLoginEmail] = useState("");
  const [loginOtp, setLoginOtp] = useState("");
  const [otpLoginSent, setOtpLoginSent] = useState(false);
  const [loginOtpCooldown, setLoginOtpCooldown] = useState(0);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [showResetPasswordForm, setShowResetPasswordForm] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0); // Countdown timer for OTP resend
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const { setIsAuthenticated } = useContext(AuthContext);

  // Utility functions for token encryption/decryption in cookies
  const TOKEN_COOKIE_KEY = 'token';
  const SECRET_KEY = 'tabtrips_hotel_secret';
  function setEncryptedToken(token) {
    const encrypted = CryptoJS.AES.encrypt(token, SECRET_KEY).toString();
    Cookies.set(TOKEN_COOKIE_KEY, encrypted, { secure: true, sameSite: 'strict' });
  }
  function removeToken() {
    Cookies.remove(TOKEN_COOKIE_KEY);
  }

  const showToast = (message, type = "info") => {
    if (type === "success") {
      toast.success(message);
    } else if (type === "error") {
      toast.error(message);
    } else {
      toast.info(message);
    }
  };

  useEffect(() => {
    let interval;
    if (otpCooldown > 0 || loginOtpCooldown > 0) {
      interval = setInterval(() => {
        setOtpCooldown((prev) => (prev > 0 ? prev - 1 : 0));
        setLoginOtpCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpCooldown, loginOtpCooldown]);

  const completeLogin = async (data, loginEmail = '') => {
    const draftSnapshot = preserveOnboardingDraft();
    clearUserSession();
    resetHotelListCache();
    restoreOnboardingDraft(draftSnapshot);
    markAccountComplete(loginEmail, data.host_id);
    setEncryptedToken(data.token);
    if (data.host_id) {
      localStorage.setItem('selectedHostId', data.host_id);
    }
    setIsAuthenticated(true);
    showToast("Login successful!", "success");

    try {
      await prefetchHotelsAfterAuth();
    } catch (err) {
      console.error('Failed to load hotels after login:', err);
    }

    const redirectPath = sessionStorage.getItem('redirectPath');
    if (redirectPath) {
      sessionStorage.removeItem('redirectPath');
      navigate(redirectPath);
    } else if (hasAddPropertyDraftToResume(loginEmail)) {
      navigate('/add-property');
    } else if (hasListingDraftToResume(loginEmail)) {
      navigate('/listing');
    } else {
      navigate('/');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      showToast("Please enter email and password", "error");
      return;
    }
    
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('email', email);
      formData.append('password', password);

      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/login',
        formData
      );

      if (response.data.status) {
        completeLogin(response.data, email);
      } else {
        // More specific error message for wrong password
        const errorMessage = response.data.message || 'Invalid credentials';
        if (errorMessage.toLowerCase().includes('password') || errorMessage.toLowerCase().includes('invalid')) {
          showToast("Your password is wrong. Please check and try again.", "error");
        } else {
          showToast(errorMessage, "error");
        }
      }
    } catch (error) {
      console.error('API Error:', error);
      if (error.response && error.response.status === 401) {
        showToast("Your password is wrong. Please check and try again.", "error");
      } else if (error.response && error.response.data && error.response.data.message) {
        showToast(error.response.data.message, "error");
      } else {
        showToast('An error occurred. Please check your internet connection and try again.', "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      showToast("Please enter your email address", "error");
      return;
    }
    
    try {
      // Create FormData object
      const formData = new FormData();
      formData.append("email", forgotEmail);
  
      // Make the POST request with FormData
      const response = await fetch(
        "https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/generateOTP",
        {
          method: "POST",
          body: formData, // Use FormData instead of JSON
        }
      );
  
      // Parse the response as JSON
      const data = await response.json();
  
      // Check both HTTP status and API response status
      if (response.ok && data.status === true) {
        showToast("OTP sent successfully!", "success");
        setShowOtpVerification(true);
        setOtpCooldown(60); // Start 60-second cooldown
      } else {
        // Show error message from the API or a generic error
        showToast(data.message || "Failed to send OTP", "error");
      }
    } catch (error) {
      showToast("An error occurred. Please try again.", "error");
    }
  };
  
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      showToast("Please enter a valid 6-digit OTP", "error");
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append("email", forgotEmail);
      formData.append("otp", otp);

      const response = await fetch(
        "https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/verifyOtp",
        {
          method: "POST",
          body: formData
        }
      );
      
      const data = await response.json();
      if (response.ok && data.status === true) {
        showToast("OTP verified successfully!", "success");
        setShowResetPasswordForm(true);
        setShowForgotPasswordForm(false); // Hide email input and Send OTP button
        setShowOtpVerification(false); // Hide OTP input and Verify OTP button
      } else {
        showToast(data.message || "Invalid OTP", "error");
      }
    } catch (error) {
      showToast("An error occurred. Please try again.", "error");
    }
  };

  const handleStartOtpLogin = () => {
    setOtpLoginEmail(email.trim());
    setLoginOtp("");
    setOtpLoginSent(false);
    setShowOtpLogin(true);
  };

  const handleBackFromOtpLogin = () => {
    setShowOtpLogin(false);
    setOtpLoginSent(false);
    setLoginOtp("");
  };

  const handleSendLoginOtp = async () => {
    const emailToUse = otpLoginEmail.trim();
    if (!emailToUse) {
      showToast("Please enter your email address", "error");
      return;
    }

    try {
      setLoading(true);
      const data = await sendLoginOtp(emailToUse);
      if (data.status) {
        showToast(data.message || "OTP sent successfully!", "success");
        setOtpLoginSent(true);
        setLoginOtpCooldown(60);
      } else {
        showToast(data.message || "Failed to send OTP", "error");
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "An error occurred. Please try again.";
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLoginWithOtp = async (e) => {
    if (e) e.preventDefault();

    const emailToUse = otpLoginEmail.trim();
    if (!emailToUse) {
      showToast("Please enter your email address", "error");
      return;
    }
    if (!loginOtp || loginOtp.length !== 6) {
      showToast("Please enter a valid 6-digit OTP", "error");
      return;
    }

    try {
      setLoading(true);
      const data = await loginWithOtp({ email: emailToUse, otp: loginOtp });
      if (data.status) {
        completeLogin(data, emailToUse);
      } else {
        showToast(data.message || "Invalid OTP", "error");
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "An error occurred. Please try again.";
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const onGoogleCredential = async (response) => {
    const idToken = response?.credential;
    if (!idToken) { showToast('Google sign-in failed', 'error'); return; }
    try {
      setGoogleLoading(true);
      const data = await loginByGoogle(idToken);
      if (data.status) {
        completeLogin(data, data.email || '');
      } else {
        showToast(data.message || 'Google sign-in failed', 'error');
      }
    } catch (err) {
      showToast(err?.response?.data?.message || 'Google sign-in failed', 'error');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!window.google || !window.google.accounts || !window.google.accounts.id) {
      showToast('Google sign-in is still loading, please try again in a moment', 'error');
      return;
    }
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: onGoogleCredential,
      ux_mode: 'popup',
    });
    window.google.accounts.id.prompt();
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      showToast("Please enter new password and confirm password", "error");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      showToast("Passwords do not match", "error");
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append("email", forgotEmail);
      formData.append("new_password", newPassword);
      formData.append("confirm_password", confirmPassword);

      const response = await fetch(
        "https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/resetPassword",
        {
          method: "POST",
          body: formData
        }
      );
      const data = await response.json();
      if (response.ok && data.status === true) {
        showToast("Password reset successful!", "success");
        setShowForgotPasswordForm(false);
        setShowOtpVerification(false);
        setShowResetPasswordForm(false);
      } else {
        showToast(data.message || "Failed to reset password", "error");
      }
    } catch (error) {
      showToast("An error occurred. Please try again.", "error");
    }
  };

  function clearUserSession() {
    // Remove token from cookies
    removeToken();
    // Remove all user/session data from localStorage
    localStorage.removeItem('selectedHotelId');
    localStorage.removeItem('selectedHotelName');
    localStorage.removeItem('selectedHostId');
    localStorage.removeItem('hotelsList');
    // Add any other keys you use for user session here
    sessionStorage.clear(); // Optionally clear sessionStorage as well
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-card-header">
          <h2 className="login-title">Sign in to manage your property</h2>
          <p className="login-subtitle">Welcome back! Please enter your details.</p>
        </div>

        {!showForgotPasswordForm && !showOtpVerification && !showResetPasswordForm && !showOtpLogin && (
          <>
            <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Username/Email address</label>
              <input
                type="email"
                placeholder="Enter your username or email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <div className="forgot-password">
                <a href="#" onClick={(e) => {
                  e.preventDefault();
                  setShowForgotPasswordForm(true);
                }}>
                  Forgot your password?
                </a>
              </div>
            </div>

            <div className="login-actions-row">
              <button type="submit" className="login-button" disabled={loading}>
                {loading ? "Signing in..." : "Sign in"}
              </button>
              <button
                type="button"
                className="otp-login-button"
                onClick={handleStartOtpLogin}
                disabled={loading}
              >
                Login via OTP
              </button>
            </div>
          </form>

          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0' }}>
              <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
              <span style={{ color: '#64748b', fontSize: '0.875rem' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
            </div>
            <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 12px 0' }}>Don't have an account?</p>
            <button
              type="button"
              onClick={() => navigate('/listing')}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #FF7700',
                background: 'transparent',
                color: '#FF7700',
                fontWeight: '600',
                fontSize: '1rem',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
            >
              Create Account
            </button>
          </div>
          {GOOGLE_LOGIN_ENABLED && (
            <div style={{ marginTop: '12px' }}>
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #dadce0',
                  background: '#ffffff',
                  color: '#3c4043',
                  fontWeight: '500',
                  fontSize: '1rem',
                  cursor: googleLoading ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  opacity: googleLoading ? 0.7 : 1,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.982 5.482 18 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.176 0 7.548 0 9s.347 2.824.957 4.042l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.018.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                </svg>
                {googleLoading ? 'Signing in...' : 'Sign in with Google'}
              </button>
            </div>
          )}
          </>
        )}

        {showOtpLogin && (
          <div>
            <div className="form-group">
              <label>Email address</label>
              <input
                type="email"
                placeholder="Enter your email address"
                value={otpLoginEmail}
                onChange={(e) => setOtpLoginEmail(e.target.value)}
                disabled={loading}
              />
            </div>

            {!otpLoginSent ? (
              <button
                type="button"
                className="login-button"
                onClick={handleSendLoginOtp}
                disabled={loading || loginOtpCooldown > 0}
              >
                {loading
                  ? "Sending OTP..."
                  : loginOtpCooldown > 0
                    ? `Resend OTP (${loginOtpCooldown}s)`
                    : "Send OTP"}
              </button>
            ) : (
              <form onSubmit={handleLoginWithOtp}>
                <div className="form-group">
                  <label>Enter OTP</label>
                  <input
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={loginOtp}
                    onChange={(e) => setLoginOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    inputMode="numeric"
                    disabled={loading}
                  />
                </div>
                <button type="submit" className="login-button" disabled={loading}>
                  {loading ? "Signing in..." : "Login"}
                </button>
                <div className="resend-otp">
                  <button
                    type="button"
                    className="resend-button"
                    onClick={handleSendLoginOtp}
                    disabled={loading || loginOtpCooldown > 0}
                  >
                    {loginOtpCooldown > 0
                      ? `Resend OTP (${loginOtpCooldown}s)`
                      : "Resend OTP"}
                  </button>
                </div>
              </form>
            )}

            <div className="back-link">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleBackFromOtpLogin();
                }}
              >
                Back to login
              </a>
            </div>
          </div>
        )}

        {showForgotPasswordForm && !showOtpVerification && !showResetPasswordForm && (
          <div>
            <div className="form-group">
              <label>Enter your email address</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
              />
            </div>
            <button
              className="login-button"
              onClick={handleForgotPassword}
              disabled={otpCooldown > 0}
            >
              {otpCooldown > 0 ? `Resend OTP (${otpCooldown}s)` : "Send OTP"}
            </button>
            <div className="back-link">
              <a href="#" onClick={(e) => {
                e.preventDefault();
                setShowForgotPasswordForm(false);
              }}>
                Back to login
              </a>
            </div>
          </div>
        )}

        {showOtpVerification && (
          <div>
            <div className="form-group">
              <label>Enter OTP</label>
              <input
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
              />
            </div>
            <button className="login-button" onClick={handleVerifyOtp}>
              Verify OTP
            </button>
            <div className="resend-otp">
              <button
                className="resend-button"
                onClick={handleForgotPassword}
                disabled={otpCooldown > 0}
              >
                {otpCooldown > 0 ? `Resend OTP (${otpCooldown}s)` : "Resend OTP"}
              </button>
            </div>
          </div>
        )}

        {showResetPasswordForm && (
          <div>
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            <button className="login-button" onClick={handleResetPassword}>
              Reset Password
            </button>
          </div>
        )}
      </div>

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default LoginCard;